// frontend/src/pages/AdminAgentPage.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import { invokeFunction, dbSelect, dbUpdate, dbDelete } from '../lib/api';
import { VISIBLE_AGENTIC_CODER_MODELS, type VisibleAgenticCoderModelId } from '../lib/agentModels';
import { AgentBacklogItem } from '../lib/supabase';
import {
  Bot,
  Play,
  Square,
  Monitor,
  Terminal,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  List,
  Layers,
  Trash2,
  Eye,
  X,
  RotateCcw,
  Check,
  Cpu,
} from 'lucide-react';

interface LogEntry {
  id: number;
  type: 'status' | 'tool_call' | 'tool_result' | 'agent_thought' | 'complete' | 'failed' | 'error' | 'screenshot';
  timestamp: Date;
  data: Record<string, unknown>;
}

const ADMIN_EMAIL = 'a.burkov@gmail.com';

const AGENT_MODELS = VISIBLE_AGENTIC_CODER_MODELS;

type AgentModelId = VisibleAgenticCoderModelId;

const AGENT_MODEL_STORAGE_KEY = 'llambada-agent-model';

function getStoredAgentModel(): AgentModelId {
  const stored = localStorage.getItem(AGENT_MODEL_STORAGE_KEY);
  if (stored && AGENT_MODELS.some(m => m.id === stored)) return stored as AgentModelId;
  return 'kimi-for-coding';
}

function setStoredAgentModel(modelId: AgentModelId) {
  localStorage.setItem(AGENT_MODEL_STORAGE_KEY, modelId);
}

interface ScreenshotEntry {
  image: string;
  label: string;
}

interface RunAgentOptions {
  skipValidation?: boolean;
  model?: string;
  autoGenerateTitle?: boolean;
  useRawDescription?: boolean;
  sourceZipName?: string;
  sourceZipContentBase64?: string;
}

function normalizePrivateProjectUrl(rawUrl: string): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    const match = parsed.pathname.match(/\/project\/([0-9a-fA-F-]{36})/i);
    if (!match) return null;
    return `${parsed.origin}/project/${match[1]}`;
  } catch (_) {
    const pathMatch = trimmed.match(/\/project\/([0-9a-fA-F-]{36})/i);
    if (!pathMatch) return null;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return origin ? `${origin}/project/${pathMatch[1]}` : `/project/${pathMatch[1]}`;
  }
}

function extractProjectId(url: string): string | null {
  const match = url.match(/\/project\/([0-9a-fA-F-]{36})/i);
  return match ? match[1] : null;
}

function extractPrivateProjectUrl(text: string): string | null {
  const absoluteMatch = text.match(/https?:\/\/[^\s"'<>]+\/project\/[0-9a-fA-F-]{36}[^\s"'<>]*/i);
  if (absoluteMatch?.[0]) {
    return normalizePrivateProjectUrl(absoluteMatch[0]);
  }

  const relativeMatch = text.match(/\/project\/[0-9a-fA-F-]{36}(?:\/conversation\/[0-9a-fA-F-]{36})?/i);
  if (relativeMatch?.[0]) {
    return normalizePrivateProjectUrl(relativeMatch[0]);
  }

  return null;
}

function formatItemDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ============================================================================
// Shared agent runner hook
// ============================================================================

function useAgentRunner() {
  const { session, ensureFreshToken } = useAuth();
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [screenshotState, setScreenshotState] = useState<{ items: ScreenshotEntry[]; index: number }>({
    items: [],
    index: -1,
  });
  const [finalStatus, setFinalStatus] = useState<'idle' | 'complete' | 'failed' | 'error'>('idle');
  const [finalMessage, setFinalMessage] = useState('');
  const [projectUrl, setProjectUrl] = useState('');
  const [logsExpanded, setLogsExpanded] = useState(true);

  const logEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const logIdRef = useRef(0);

  const addLog = useCallback((type: LogEntry['type'], data: Record<string, unknown>) => {
    const entry: LogEntry = {
      id: logIdRef.current++,
      type,
      timestamp: new Date(),
      data,
    };
    setLogs((prev) => [...prev, entry]);
  }, []);



  const reset = useCallback(() => {
    setLogs([]);
    setScreenshotState({ items: [], index: -1 });
    setFinalStatus('idle');
    setFinalMessage('');
    setProjectUrl('');
    logIdRef.current = 0;
  }, []);

  const runAgent = useCallback(async (title: string, description: string, options: RunAgentOptions = {}): Promise<{
    status: 'complete' | 'failed' | 'error';
    message: string;
    projectUrl: string;
    logs: LogEntry[];
    screenshots: ScreenshotEntry[];
  }> => {
    if (!session) throw new Error('No session');

    reset();
    setRunning(true);

    const collectedLogs: LogEntry[] = [];
    const collectedScreenshots: ScreenshotEntry[] = [];
    let resultStatus: 'complete' | 'failed' | 'error' = 'error';
    let resultMessage = '';
    let resultProjectUrl = '';

    const localAddLog = (type: LogEntry['type'], data: Record<string, unknown>) => {
      const entry: LogEntry = {
        id: logIdRef.current++,
        type,
        timestamp: new Date(),
        data,
      };
      collectedLogs.push(entry);
      setLogs((prev) => [...prev, entry]);
    };

    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      localAddLog('status', { message: 'Authenticating...' });

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const accessToken = await ensureFreshToken();
      if (!accessToken) {
        throw new Error('Session expired. Please sign in again.');
      }

      const authResponse = await fetch(
        `${supabaseUrl}/functions/v1/browser-agent-auth`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(supabaseAnonKey ? { 'apikey': supabaseAnonKey } : {}),
          },
          body: JSON.stringify({}),
          signal: abortController.signal,
        }
      );

      if (!authResponse.ok) {
        let errorDetail = 'Authentication failed';
        try {
          const errorData = await authResponse.json();
          errorDetail = errorData.error || errorDetail;
        } catch (_) {
          // ignore parse errors
        }
        throw new Error(`${errorDetail} (HTTP ${authResponse.status})`);
      }

      const { token, serviceUrl } = await authResponse.json();

      localAddLog('status', { message: 'Connected. Starting browser agent...' });

      const response = await fetch(`${serviceUrl}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          skip_validation: options.skipValidation || false,
          model: options.model || undefined,
          auto_generate_title: options.autoGenerateTitle || false,
          use_raw_description: options.useRawDescription || false,
          source_zip_name: options.sourceZipName || undefined,
          source_zip_content_base64: options.sourceZipContentBase64 || undefined,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to connect to browser agent';
        try {
          const text = await response.text();
          const match = text.match(/data:\s*(\{.*\})/);
          if (match) {
            const data = JSON.parse(match[1]);
            if (data.message) errorMessage = data.message;
          }
        } catch (_) { /* ignore */ }
        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = '';

      let chunkCount = 0;
      let streamDone = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('[BrowserAgent] Stream reader done. chunkCount=', chunkCount, 'bufferLen=', buffer.length);
          break;
        }

        chunkCount++;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            let data;
            try {
              data = JSON.parse(line.slice(6));
            } catch (_) {
              console.warn('[BrowserAgent] JSON parse failed for event:', eventType, 'line:', line.slice(0, 200));
              eventType = '';
              continue;
            }

            console.log('[BrowserAgent] SSE event:', eventType, 'data:', JSON.stringify(data).slice(0, 200));

            switch (eventType) {
              case 'screenshot':
                {
                  const entry = { image: String(data.image || ''), label: String(data.label || '') };
                  collectedScreenshots.push(entry);
                  setScreenshotState((prev) => {
                    const items = [...prev.items, entry];
                    return { items, index: items.length - 1 };
                  });
                }
                break;

              case 'status':
              case 'tool_call':
              case 'tool_result':
              case 'agent_thought':
                if (eventType === 'status' && typeof data.message === 'string') {
                  const extractedUrl = extractPrivateProjectUrl(data.message);
                  if (extractedUrl) {
                    resultProjectUrl = resultProjectUrl || extractedUrl;
                    setProjectUrl((prev) => prev || extractedUrl);
                  }
                }
                if (eventType === 'tool_result' && typeof data.result === 'string') {
                  const extractedUrl = extractPrivateProjectUrl(data.result);
                  if (extractedUrl) {
                    resultProjectUrl = resultProjectUrl || extractedUrl;
                    setProjectUrl((prev) => prev || extractedUrl);
                  }
                }
                localAddLog(eventType as LogEntry['type'], data);
                break;

              case 'complete':
                localAddLog('complete', data);
                resultStatus = 'complete';
                resultMessage = data.summary || 'Mini-app created successfully!';
                setFinalStatus('complete');
                setFinalMessage(resultMessage);
                {
                  const payloadUrl =
                    (typeof data.project_url === 'string' && data.project_url) ||
                    (typeof data.projectUrl === 'string' && data.projectUrl) ||
                    '';
                  const normalizedPayloadUrl = payloadUrl
                    ? normalizePrivateProjectUrl(payloadUrl)
                    : null;
                  const summaryUrl = extractPrivateProjectUrl(String(data.summary || ''));
                  const resolvedUrl = normalizedPayloadUrl || summaryUrl;
                  if (resolvedUrl) {
                    resultProjectUrl = resolvedUrl;
                    setProjectUrl(resolvedUrl);
                  }
                }
                break;

              case 'failed':
                localAddLog('failed', data);
                resultStatus = 'failed';
                resultMessage = data.reason || 'Agent failed';
                setFinalStatus('failed');
                setFinalMessage(resultMessage);
                break;

              case 'error':
                localAddLog('error', data);
                resultStatus = 'error';
                resultMessage = data.message || 'An error occurred';
                setFinalStatus('error');
                setFinalMessage(resultMessage);
                break;

              case 'done':
                console.log('[BrowserAgent] Received done event — ending stream read');
                streamDone = true;
                break;
            }

            eventType = '';
          }
        }

        // If the server sent the 'done' event, stop reading immediately
        // (the server may keep the connection open due to a bug).
        if (streamDone) {
          console.log('[BrowserAgent] Breaking reader loop after done event');
          break;
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        console.log('[BrowserAgent:runAgent] Caught AbortError (stopped by user)');
        localAddLog('status', { message: 'Agent stopped by user' });
        resultStatus = 'failed';
        resultMessage = 'Stopped by user';
        setFinalStatus('failed');
        setFinalMessage('Stopped by user');
      } else {
        console.error('[BrowserAgent:runAgent] Caught error:', (err as Error).message, err);
        localAddLog('error', { message: (err as Error).message });
        resultStatus = 'error';
        resultMessage = (err as Error).message;
        setFinalStatus('error');
        setFinalMessage(resultMessage);
      }
    } finally {
      console.log('[BrowserAgent:runAgent] finally block — resultStatus:', resultStatus, 'resultProjectUrl:', resultProjectUrl);
      setRunning(false);
      abortRef.current = null;
    }

    console.log('[BrowserAgent:runAgent] Returning result:', { status: resultStatus, message: resultMessage.slice(0, 100), projectUrl: resultProjectUrl });
    return {
      status: resultStatus,
      message: resultMessage,
      projectUrl: resultProjectUrl,
      logs: collectedLogs,
      screenshots: collectedScreenshots,
    };
  }, [session, ensureFreshToken, reset]);

  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  const currentScreenshot = screenshotState.index >= 0 ? screenshotState.items[screenshotState.index] : null;
  const canGoPrev = screenshotState.index > 0;
  const canGoNext = screenshotState.index >= 0 && screenshotState.index < screenshotState.items.length - 1;

  return {
    running,
    logs,
    screenshotState,
    setScreenshotState,
    finalStatus,
    finalMessage,
    projectUrl,
    logsExpanded,
    setLogsExpanded,
    logEndRef,
    addLog,
    reset,
    runAgent,
    handleStop,
    currentScreenshot,
    canGoPrev,
    canGoNext,
  };
}

// ============================================================================
// Main page component
// ============================================================================

export function AdminAgentPage() {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'single' | 'multiple' | 'zipBatch'>('single');

  useEffect(() => {
    document.title = 'Browser Agent - Llambada';
    return () => {
      document.title = 'Llambada';
    };
  }, []);

  if (authLoading) return <PageLoader />;

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
            <Bot className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Browser Agent
            </h1>
            <p className="text-theme-text-secondary">
              Automatically create mini-apps using an AI-driven browser agent
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-theme-border">
          <button
            onClick={() => setActiveTab('single')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'single'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-theme-text-muted hover:text-theme-text hover:border-theme-border-muted'
              }`}
          >
            <Bot className="h-4 w-4" />
            Single app
          </button>
          <button
            onClick={() => setActiveTab('multiple')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'multiple'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-theme-text-muted hover:text-theme-text hover:border-theme-border-muted'
              }`}
          >
            <Layers className="h-4 w-4" />
            Multiple apps
          </button>
          <button
            onClick={() => setActiveTab('zipBatch')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'zipBatch'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-theme-text-muted hover:text-theme-text hover:border-theme-border-muted'
              }`}
          >
            <Layers className="h-4 w-4" />
            Zip batch edits
          </button>
        </div>

        {activeTab === 'single' ? (
          <SingleAppTab />
        ) : activeTab === 'multiple' ? (
          <MultipleAppsTab />
        ) : (
          <ZipBatchTab />
        )}
      </div>
    </Layout>
  );
}

// ============================================================================
// Single App Tab (original UX)
// ============================================================================

function SingleAppTab() {
  const { session, ensureFreshToken } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [selectedModel, setSelectedModel] = useState<AgentModelId>(getStoredAgentModel);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  const agent = useAgentRunner();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleModelChange = (modelId: AgentModelId) => {
    setSelectedModel(modelId);
    setStoredAgentModel(modelId);
    setModelDropdownOpen(false);
  };

  const handleStart = async () => {
    if (!title.trim() || !session) return;
    await agent.runAgent(title, description, { model: selectedModel });
  };

  const handleGenerateDescription = async () => {
    if (!session || !title.trim()) return;

    setGeneratingDescription(true);
    setGenerateError('');

    try {
      const accessToken = await ensureFreshToken();
      if (!accessToken) {
        throw new Error('Session expired. Please sign in again.');
      }

      const { data, error } = await invokeFunction<{ specification?: string }>(
        'generate-description',
        {
          action: 'agent_specification',
          title: title.trim(),
          description: description.trim() || null,
        },
        { token: accessToken }
      );

      if (error) {
        throw error;
      }

      const generatedSpecification = data?.specification?.trim();
      if (!generatedSpecification) {
        throw new Error('No specification generated');
      }

      setDescription(generatedSpecification);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate specification';
      setGenerateError(message);
    } finally {
      setGeneratingDescription(false);
    }
  };

  return (
    <>
      {/* Input Form */}
      <div className="card p-6 mb-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
              Mini-App Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input"
              placeholder="e.g. Pomodoro Timer"
              disabled={agent.running}
            />
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <label htmlFor="description" className="block text-sm font-medium text-theme-text-secondary">
                Description (optional, from the user's perspective)
              </label>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={agent.running || generatingDescription || !title.trim()}
                className="inline-flex items-center gap-1 text-xs font-medium text-theme-text-secondary transition-colors hover:text-theme-text disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generatingDescription ? <LoadingSpinner size="sm" /> : <Sparkles className="h-3.5 w-3.5" />}
                <span>{generatingDescription ? 'Generating...' : 'Generate'}</span>
              </button>
            </div>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input min-h-[100px]"
              placeholder="e.g. I want a simple pomodoro timer with 25-minute work sessions and 5-minute breaks. It should show a large countdown timer, play a sound when the timer ends, and track how many pomodoros I've completed today."
              disabled={agent.running || generatingDescription}
              rows={4}
            />
            {generateError && (
              <p className="mt-1 text-xs text-theme-error">{generateError}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!agent.running ? (
              <button
                onClick={handleStart}
                disabled={!title.trim()}
                className="btn btn-primary flex items-center gap-2"
              >
                <Play className="h-4 w-4" />
                Launch Agent
              </button>
            ) : (
              <button
                onClick={agent.handleStop}
                className="btn btn-danger flex items-center gap-2"
              >
                <Square className="h-4 w-4" />
                Stop Agent
              </button>
            )}
            <div className="relative" ref={modelDropdownRef}>
              <button
                type="button"
                onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                disabled={agent.running}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-sm"
                title="Select coding model"
              >
                <Cpu className="h-3.5 w-3.5 text-theme-text-muted" />
                <span className="font-medium text-theme-text">
                  {AGENT_MODELS.find(m => m.id === selectedModel)?.shortName || 'Kimi'}
                </span>
                <ChevronDown className={'h-3.5 w-3.5 text-theme-text-muted transition-transform ' + (modelDropdownOpen ? 'rotate-180' : '')} />
              </button>
              {modelDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-52 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                  {AGENT_MODELS.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => handleModelChange(model.id)}
                      className={'w-full flex items-center justify-between px-3 py-2 text-left hover:bg-theme-surface-hover transition-colors ' +
                        (selectedModel === model.id ? 'bg-theme-surface-hover' : '')
                      }
                    >
                      <div className="text-sm font-medium text-theme-text">{model.name}</div>
                      {selectedModel === model.id && <Check className="h-3.5 w-3.5 text-accent-500" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AgentOutputPanel agent={agent} />
    </>
  );
}

// ============================================================================
// Multiple Apps Tab
// ============================================================================

function MultipleAppsTab() {
  const { session, ensureFreshToken } = useAuth();
  const [bulkText, setBulkText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [selectedModel, setSelectedModel] = useState<AgentModelId>(getStoredAgentModel);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  // Backlog state
  const [backlog, setBacklog] = useState<AgentBacklogItem[]>([]);
  const [loadingBacklog, setLoadingBacklog] = useState(true);

  // Building state
  const [building, setBuilding] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const buildingRef = useRef(false);
  const stopRequestedRef = useRef(false);

  // Log viewer modal
  const [viewingLog, setViewingLog] = useState<AgentBacklogItem | null>(null);

  // Backlog tab state
  const [backlogTab, setBacklogTab] = useState<'backlog' | 'succeeded' | 'failed'>('backlog');

  const agent = useAgentRunner();

  // Keep refs to avoid stale closures in the long-running startBuilding loop.
  // agent.runAgent and ensureFreshToken are recreated on every render, but
  // startBuilding captures them only once. Using refs ensures the loop always
  // calls the latest versions.
  const runAgentRef = useRef(agent.runAgent);
  runAgentRef.current = agent.runAgent;
  const ensureFreshTokenRef = useRef(ensureFreshToken);
  ensureFreshTokenRef.current = ensureFreshToken;
  const selectedModelRef = useRef(selectedModel);
  selectedModelRef.current = selectedModel;

  // Close model dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleModelChange = (modelId: AgentModelId) => {
    setSelectedModel(modelId);
    setStoredAgentModel(modelId);
    setModelDropdownOpen(false);
  };

  // Fetch backlog on mount
  useEffect(() => {
    if (session) fetchBacklog();
  }, [session]);

  const fetchBacklog = async () => {
    if (!session) return;
    setLoadingBacklog(true);
    try {
      const accessToken = await ensureFreshToken({ forceRefresh: false }) || session.access_token;

      const { data, error } = await dbSelect<AgentBacklogItem[]>('agent_backlog', {
        token: accessToken,
        order: 'created_at.desc',
      });
      if (error) throw error;
      const items = data || [];

      // If any item is in 'building' status but we're not building, reset it to pending
      // (this handles page refresh during a build)
      const staleBuilding = items.filter((item) => item.status === 'building');
      if (staleBuilding.length > 0) {
        for (const item of staleBuilding) {
          await dbUpdate('agent_backlog', { status: 'pending' }, { id: `eq.${item.id}` }, { token: accessToken });
        }
        // Re-fetch after fixing stale items
        const { data: refreshed } = await dbSelect<AgentBacklogItem[]>('agent_backlog', {
          token: accessToken,
          order: 'created_at.desc',
        });
        setBacklog(refreshed || []);
      } else {
        setBacklog(items);
      }
    } catch (err) {
      console.error('Failed to fetch backlog:', err);
    } finally {
      setLoadingBacklog(false);
    }
  };

  // Parse bulk text into title/description pairs
  const parseBulkText = (text: string): { title: string; description: string }[] => {
    const items: { title: string; description: string }[] = [];
    const lines = text.split('\n');
    let i = 0;
    while (i < lines.length) {
      // Skip blank lines
      if (lines[i].trim() === '') {
        i++;
        continue;
      }
      const title = lines[i].trim();
      i++;
      // Next non-blank line immediately after title (before a blank line) is the description
      let description = '';
      if (i < lines.length && lines[i].trim() !== '') {
        description = lines[i].trim();
        i++;
      }
      items.push({ title, description });
    }
    return items;
  };

  const handleSubmitBulk = async () => {
    if (!session || !bulkText.trim()) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const accessToken = await ensureFreshToken();
      if (!accessToken) throw new Error('Session expired');

      const items = parseBulkText(bulkText);
      if (items.length === 0) {
        setSubmitError('No items found. Enter at least one title.');
        setSubmitting(false);
        return;
      }

      const rows = items.map((item) => ({
        title: item.title,
        description: item.description || null,
        status: 'pending',
      }));

      // Batch insert via REST API
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/rest/v1/agent_backlog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'return=representation',
        },
        body: JSON.stringify(rows),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to insert');
      }

      setBulkText('');
      await fetchBacklog();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!session) return;
    try {
      await dbDelete('agent_backlog', { id: `eq.${id}` }, { token: session.access_token });
      setBacklog((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleRetryItem = async (id: string) => {
    if (!session) return;
    try {
      await dbUpdate(
        'agent_backlog',
        { status: 'pending', agent_log: null, screenshot_paths: null, error_message: null },
        { id: `eq.${id}` },
        { token: session.access_token }
      );
      setBacklog((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, status: 'pending' as const, agent_log: null, screenshot_paths: null, error_message: null }
            : item
        )
      );
    } catch (err) {
      console.error('Failed to retry:', err);
    }
  };

  const handleClearCompleted = async () => {
    if (!session) return;
    try {
      await dbDelete('agent_backlog', { status: 'eq.success' }, { token: session.access_token });
      setBacklog((prev) => prev.filter((item) => item.status !== 'success'));
    } catch (err) {
      console.error('Failed to clear completed:', err);
    }
  };

  // Save screenshots to Supabase storage and return paths
  const saveScreenshotsToStorage = async (backlogId: string, screenshots: ScreenshotEntry[]): Promise<string[]> => {
    if (screenshots.length === 0) return [];
    const accessToken = await ensureFreshToken();
    if (!accessToken) return [];

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const paths: string[] = [];

    for (let i = 0; i < screenshots.length; i++) {
      const screenshot = screenshots[i];
      if (!screenshot.image) continue;

      const storagePath = `${backlogId}/${String(i).padStart(3, '0')}.png`;
      try {
        const binaryStr = atob(screenshot.image);
        const bytes = new Uint8Array(binaryStr.length);
        for (let j = 0; j < binaryStr.length; j++) {
          bytes[j] = binaryStr.charCodeAt(j);
        }
        const blob = new Blob([bytes], { type: 'image/png' });

        const uploadResponse = await fetch(
          `${supabaseUrl}/storage/v1/object/agent-backlog/${storagePath}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              ...(supabaseAnonKey ? { 'apikey': supabaseAnonKey } : {}),
            },
            body: blob,
          }
        );

        if (uploadResponse.ok) {
          paths.push(storagePath);
        }
      } catch (err) {
        console.error('Failed to upload screenshot:', err);
      }
    }

    return paths;
  };

  // Helper: get a fresh token with retries (uses ref to avoid stale closures)
  const getFreshToken = useCallback(async (): Promise<string | null> => {
    for (let attempt = 0; attempt < 3; attempt++) {
      // Don't force-refresh — let ensureFreshToken decide based on expiry.
      // Force-refreshing causes refresh_token_already_used errors because
      // retries reuse the stale refresh_token from the React state closure
      // before the new session propagates.
      const token = await ensureFreshTokenRef.current();
      if (token) return token;
      // Wait longer before retry to allow background refresh to complete
      await new Promise((r) => setTimeout(r, 5000));
    }
    return null;
  }, []);

  // Build loop — processes pending backlog items one by one
  const startBuilding = useCallback(async () => {
    if (!session || buildingRef.current) return;

    console.log('[Backlog] startBuilding: entering loop');
    setBuilding(true);
    buildingRef.current = true;
    setStopRequested(false);
    stopRequestedRef.current = false;

    try {
      while (!stopRequestedRef.current) {
        console.log('[Backlog] Loop iteration: getting token...');
        const accessToken = await getFreshToken();
        if (!accessToken) {
          console.error('[Backlog] Loop break: getFreshToken returned null');
          break;
        }

        // Find next pending item
        console.log('[Backlog] Fetching next pending item...');
        const { data: pendingItems, error: selectError } = await dbSelect<AgentBacklogItem[]>('agent_backlog', {
          token: accessToken,
          filters: { status: 'eq.pending' },
          order: 'created_at.asc',
          limit: 1,
        });

        if (selectError) {
          console.error('[Backlog] Loop break: dbSelect error:', selectError);
        }
        if (!pendingItems || pendingItems.length === 0) {
          console.log('[Backlog] Loop break: no more pending items');
          break;
        }

        const item = pendingItems[0];
        console.log('[Backlog] Processing item:', item.id, item.title);

        // Mark as building
        const { error: buildingError } = await dbUpdate(
          'agent_backlog',
          { status: 'building' },
          { id: `eq.${item.id}` },
          { token: accessToken }
        );
        if (buildingError) {
          console.error('[Backlog] Failed to mark item as building:', buildingError);
        }
        setBacklog((prev) =>
          prev.map((b) => (b.id === item.id ? { ...b, status: 'building' as const } : b))
        );

        // Run agent (use ref to always call the latest runAgent)
        console.log('[Backlog] Calling runAgent for:', item.title, 'model:', selectedModelRef.current);
        const result = await runAgentRef.current(item.title, item.description || '', { skipValidation: false, model: selectedModelRef.current });
        console.log('[Backlog] runAgent returned:', { status: result.status, message: result.message?.slice(0, 100), projectUrl: result.projectUrl });

        console.log('[Backlog] Getting fresh token after agent run...');
        const freshToken = await getFreshToken();
        if (!freshToken) {
          console.error('[Backlog] Loop break: getFreshToken returned null after agent run');
          break;
        }
        console.log('[Backlog] Got fresh token, updating backlog status to:', result.status === 'complete' ? 'success' : 'failed');

        if (result.status === 'complete') {
          const projectId = result.projectUrl ? extractProjectId(result.projectUrl) : null;
          const successPayload: Record<string, unknown> = {
            status: 'success',
            project_url: result.projectUrl || null,
            project_id: projectId,
          };
          const { error: successError } = await dbUpdate(
            'agent_backlog',
            successPayload,
            { id: `eq.${item.id}` },
            { token: freshToken }
          );
          if (successError) {
            console.error('[Backlog] Failed to mark item as success (attempt 1):', successError);
            // Retry with a new token
            const retryToken = await getFreshToken();
            if (retryToken) {
              const { error: retryError } = await dbUpdate(
                'agent_backlog',
                successPayload,
                { id: `eq.${item.id}` },
                { token: retryToken }
              );
              if (retryError) console.error('[Backlog] Failed to mark item as success (attempt 2):', retryError);
            }
          }
          console.log('[Backlog] Item marked as success:', item.id, 'project_id:', projectId);
          setBacklog((prev) =>
            prev.map((b) =>
              b.id === item.id
                ? { ...b, status: 'success' as const, project_url: result.projectUrl || null, project_id: projectId }
                : b
            )
          );
        } else {
          const errorMessage = result.message || 'Unknown failure';
          // If the agent failed before login (step 1), reset to pending so it retries
          const isPreLoginFailure = /at step 1(\/|$|\b)/.test(errorMessage);

          if (isPreLoginFailure) {
            console.log('[Backlog] Pre-login failure detected, resetting to pending:', item.id, errorMessage);
            const resetPayload: Record<string, unknown> = {
              status: 'pending',
              error_message: null,
              agent_log: null,
              screenshot_paths: null,
            };
            const { error: resetError } = await dbUpdate(
              'agent_backlog',
              resetPayload,
              { id: `eq.${item.id}` },
              { token: freshToken }
            );
            if (resetError) {
              console.error('[Backlog] Failed to reset item to pending:', resetError);
            }
            setBacklog((prev) =>
              prev.map((b) =>
                b.id === item.id
                  ? { ...b, status: 'pending' as const, error_message: null, agent_log: null, screenshot_paths: null }
                  : b
              )
            );
          } else {
            console.log('[Backlog] Item failed/error, saving logs. status:', result.status);
            // Failed or error — save logs and screenshots to storage
            const serializedLogs = result.logs.map((log) => ({
              type: log.type,
              timestamp: log.timestamp.toISOString(),
              data: log.data,
            }));

            const screenshotPaths = await saveScreenshotsToStorage(item.id, result.screenshots);

            const updatePayload: Record<string, unknown> = {
              status: 'failed',
              error_message: errorMessage,
              agent_log: serializedLogs,
            };
            if (screenshotPaths.length > 0) {
              updatePayload.screenshot_paths = screenshotPaths;
            }

            const { error: failError } = await dbUpdate(
              'agent_backlog',
              updatePayload,
              { id: `eq.${item.id}` },
              { token: freshToken }
            );
            if (failError) {
              console.error('[Backlog] Failed to mark item as failed (attempt 1):', failError);
              // Retry with a new token
              const retryToken = await getFreshToken();
              if (retryToken) {
                const { error: retryError } = await dbUpdate(
                  'agent_backlog',
                  updatePayload,
                  { id: `eq.${item.id}` },
                  { token: retryToken }
                );
                if (retryError) console.error('[Backlog] Failed to mark item as failed (attempt 2):', retryError);
              }
            }
            console.log('[Backlog] Item marked as failed:', item.id);
            setBacklog((prev) =>
              prev.map((b) =>
                b.id === item.id
                  ? {
                    ...b,
                    status: 'failed' as const,
                    error_message: errorMessage,
                    agent_log: serializedLogs as Record<string, unknown>[],
                    screenshot_paths: screenshotPaths.length > 0 ? screenshotPaths : null,
                  }
                  : b
              )
            );
          }
        }

        console.log('[Backlog] Waiting 2s before next iteration...');
        // Small delay before next iteration
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      console.log('[Backlog] Loop exited. stopRequested:', stopRequestedRef.current);
    } catch (err) {
      console.error('[Backlog] startBuilding caught unexpected error:', err);
    } finally {
      console.log('[Backlog] startBuilding finally block');
      setBuilding(false);
      buildingRef.current = false;
      setStopRequested(false);
      stopRequestedRef.current = false;
      // Re-fetch backlog from DB to ensure UI matches persisted state
      await fetchBacklog();
    }
  }, [session, getFreshToken]);

  const requestStop = useCallback(() => {
    setStopRequested(true);
    stopRequestedRef.current = true;
    // Also abort the current running agent so it finishes the current build
    agent.handleStop();
  }, [agent]);

  const pendingItems = backlog.filter((item) => item.status === 'pending');
  const buildingItems = backlog.filter((item) => item.status === 'building');
  const successItems = backlog.filter((item) => item.status === 'success');
  const failedItems = backlog.filter((item) => item.status === 'failed');

  return (
    <>
      {/* Bulk Input */}
      <div className="card p-6 mb-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="bulkInput" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
              Add mini-apps to backlog
            </label>
            <p className="text-xs text-theme-text-muted mb-3">
              Enter one app per block. First line is the title, second line (optional) is the description.
              Separate entries with a blank line.
            </p>
            <textarea
              id="bulkInput"
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              className="input min-h-[200px] font-mono text-sm"
              placeholder={"Pomodoro Timer\nA simple timer with 25-minute work sessions and 5-minute breaks\n\nMarkdown Editor\n\nColor Palette Generator\nGenerate harmonious color palettes with export to CSS/Tailwind"}
              disabled={submitting}
              rows={10}
            />
            {submitError && (
              <p className="mt-1 text-xs text-theme-error">{submitError}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSubmitBulk}
              disabled={!bulkText.trim() || submitting}
              className="btn btn-primary flex items-center gap-2"
            >
              {submitting ? <LoadingSpinner size="sm" /> : <List className="h-4 w-4" />}
              {submitting ? 'Adding...' : 'Add to backlog'}
            </button>

            <div className="flex-1" />

            <div className="relative" ref={modelDropdownRef}>
              <button
                type="button"
                onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                disabled={building}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-sm"
                title="Select coding model"
              >
                <Cpu className="h-3.5 w-3.5 text-theme-text-muted" />
                <span className="font-medium text-theme-text">
                  {AGENT_MODELS.find(m => m.id === selectedModel)?.shortName || 'Kimi'}
                </span>
                <ChevronDown className={'h-3.5 w-3.5 text-theme-text-muted transition-transform ' + (modelDropdownOpen ? 'rotate-180' : '')} />
              </button>
              {modelDropdownOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-52 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                  {AGENT_MODELS.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => handleModelChange(model.id)}
                      className={'w-full flex items-center justify-between px-3 py-2 text-left hover:bg-theme-surface-hover transition-colors ' +
                        (selectedModel === model.id ? 'bg-theme-surface-hover' : '')
                      }
                    >
                      <div className="text-sm font-medium text-theme-text">{model.name}</div>
                      {selectedModel === model.id && <Check className="h-3.5 w-3.5 text-accent-500" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!building ? (
              <button
                onClick={startBuilding}
                disabled={pendingItems.length === 0}
                className="btn btn-primary flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Play className="h-4 w-4" />
                Start building ({pendingItems.length} pending)
              </button>
            ) : (
              <button
                onClick={requestStop}
                className="btn btn-danger flex items-center gap-2"
              >
                <Square className="h-4 w-4" />
                {stopRequested ? 'Stopping after current...' : 'Stop building'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Agent output (visible during building) */}
      {(agent.running || agent.logs.length > 0) && (
        <div className="mb-6">
          <AgentOutputPanel agent={agent} />
        </div>
      )}

      {/* Backlog tabs and content */}
      {loadingBacklog ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <div>
          {/* Tab bar */}
          <div className="flex gap-1 mb-4 border-b border-theme-border">
            <button
              onClick={() => setBacklogTab('backlog')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${backlogTab === 'backlog'
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-theme-text-muted hover:text-theme-text hover:border-theme-border-muted'
                }`}
            >
              <AlertCircle className="h-4 w-4" />
              Backlog
              {(pendingItems.length + buildingItems.length) > 0 && (
                <span className="text-xs bg-theme-surface px-1.5 py-0.5 rounded-full">
                  {pendingItems.length + buildingItems.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setBacklogTab('succeeded')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${backlogTab === 'succeeded'
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-theme-text-muted hover:text-theme-text hover:border-theme-border-muted'
                }`}
            >
              <CheckCircle className="h-4 w-4" />
              Succeeded
              {successItems.length > 0 && (
                <span className="text-xs bg-theme-surface px-1.5 py-0.5 rounded-full">
                  {successItems.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setBacklogTab('failed')}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${backlogTab === 'failed'
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-theme-text-muted hover:text-theme-text hover:border-theme-border-muted'
                }`}
            >
              <XCircle className="h-4 w-4" />
              Failed
              {failedItems.length > 0 && (
                <span className="text-xs bg-theme-surface px-1.5 py-0.5 rounded-full">
                  {failedItems.length}
                </span>
              )}
            </button>
          </div>

          {/* Tab content */}
          {backlogTab === 'backlog' && (
            <div className="space-y-6">
              {/* Building */}
              {buildingItems.length > 0 && (
                <BacklogSection
                  title="Building"
                  icon={<LoadingSpinner size="sm" />}
                  items={buildingItems}
                  color="text-blue-500"
                />
              )}

              {/* Pending */}
              {pendingItems.length > 0 && (
                <BacklogSection
                  title="Pending"
                  icon={<AlertCircle className="h-4 w-4" />}
                  items={pendingItems}
                  color="text-theme-text-muted"
                  onDelete={handleDeleteItem}
                />
              )}

              {/* Empty state */}
              {pendingItems.length === 0 && buildingItems.length === 0 && (
                <div className="text-center py-12 text-theme-text-muted">
                  <Layers className="h-8 w-8 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No pending items. Add some above.</p>
                </div>
              )}
            </div>
          )}

          {backlogTab === 'succeeded' && (
            <div>
              {successItems.length > 0 ? (
                <>
                  <div className="flex items-center justify-end mb-3">
                    <button
                      onClick={handleClearCompleted}
                      className="text-xs text-theme-text-muted hover:text-theme-text transition-colors"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="space-y-2">
                    {successItems.map((item) => (
                      <div key={item.id} className="card px-4 py-3 flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-theme-success flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-theme-text truncate">{item.title}</p>
                          {item.description && (
                            <p className="text-xs text-theme-text-muted truncate">{item.description}</p>
                          )}
                        </div>
                        <span className="text-xs text-theme-text-muted flex-shrink-0">{formatItemDate(item.created_at)}</span>
                        {item.project_url && (
                          <a
                            href={item.project_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-emerald-500 hover:text-emerald-400 underline flex-shrink-0"
                          >
                            Open project
                          </a>
                        )}
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="p-1 text-theme-text-muted hover:text-theme-error transition-colors flex-shrink-0"
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-theme-text-muted">
                  <CheckCircle className="h-8 w-8 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No succeeded items yet.</p>
                </div>
              )}
            </div>
          )}

          {backlogTab === 'failed' && (
            <div>
              {failedItems.length > 0 ? (
                <div className="space-y-2">
                  {failedItems.map((item) => (
                    <div key={item.id} className="card px-4 py-3 flex items-center gap-3">
                      <XCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-theme-text truncate">{item.title}</p>
                        {item.error_message && (
                          <p className="text-xs text-amber-500 dark:text-amber-400 truncate">{item.error_message}</p>
                        )}
                      </div>
                      <span className="text-xs text-theme-text-muted flex-shrink-0">{formatItemDate(item.created_at)}</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {item.agent_log && (
                          <button
                            onClick={() => setViewingLog(item)}
                            className="p-1 text-theme-text-muted hover:text-theme-text transition-colors"
                            title="View agent log"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRetryItem(item.id)}
                          className="p-1 text-theme-text-muted hover:text-blue-500 transition-colors"
                          title="Retry"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="p-1 text-theme-text-muted hover:text-theme-error transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-theme-text-muted">
                  <XCircle className="h-8 w-8 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No failed items.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Log viewer modal */}
      {viewingLog && (
        <AgentLogModal item={viewingLog} onClose={() => setViewingLog(null)} />
      )}
    </>
  );
}

// ============================================================================
// Zip Batch Tab (source zip + prompt template + variable values)
// ============================================================================

type ZipBatchItemStatus = 'pending' | 'running' | 'complete' | 'failed' | 'error' | 'stopped';

interface ZipBatchItem {
  id: number;
  value: string;
  title: string;
  prompt: string;
  status: ZipBatchItemStatus;
  message: string;
  projectUrl: string;
}

function normalizeVariableName(raw: string): string {
  return raw.trim().replace(/^%+|%+$/g, '').replace(/[^a-zA-Z0-9_]/g, '');
}

function replaceVariableTokens(template: string, variableToken: string, value: string): string {
  const escapedToken = variableToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return template
    .replace(new RegExp(escapedToken, 'g'), value)
    .replace(/%value%/g, value);
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function ZipBatchTab() {
  const { session } = useAuth();
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipBase64, setZipBase64] = useState('');
  const [promptTemplate, setPromptTemplate] = useState('');
  const [variableName, setVariableName] = useState('language');
  const [valuesText, setValuesText] = useState('');
  const [selectedModel, setSelectedModel] = useState<AgentModelId>(getStoredAgentModel);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const [batchError, setBatchError] = useState('');
  const [batchItems, setBatchItems] = useState<ZipBatchItem[]>([]);
  const [runningBatch, setRunningBatch] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const runningBatchRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const selectedModelRef = useRef(selectedModel);
  selectedModelRef.current = selectedModel;

  const agent = useAgentRunner();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleModelChange = (modelId: AgentModelId) => {
    setSelectedModel(modelId);
    setStoredAgentModel(modelId);
    setModelDropdownOpen(false);
  };

  const requestStop = useCallback(() => {
    setStopRequested(true);
    stopRequestedRef.current = true;
    agent.handleStop();
  }, [agent]);

  const startBatch = useCallback(async () => {
    if (!session || runningBatchRef.current) return;

    setBatchError('');
    const normalizedVar = normalizeVariableName(variableName);
    if (!normalizedVar) {
      setBatchError('Variable name is required and can contain only letters, numbers, and underscores.');
      return;
    }
    if (!zipFile) {
      setBatchError('Select a source zip file.');
      return;
    }
    if (!promptTemplate.trim()) {
      setBatchError('Prompt template is required.');
      return;
    }

    const values = valuesText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (values.length === 0) {
      setBatchError('Provide at least one variable value (one per line).');
      return;
    }

    const variableToken = `%${normalizedVar}%`;
    const templateHasVariable =
      promptTemplate.includes(variableToken) ||
      promptTemplate.includes('%value%');

    if (!templateHasVariable) {
      setBatchError(`Prompt template must include ${variableToken} or %value%.`);
      return;
    }

    let sourceZipContent = zipBase64;
    if (!sourceZipContent) {
      try {
        sourceZipContent = await fileToBase64(zipFile);
        setZipBase64(sourceZipContent);
      } catch (err) {
        setBatchError(err instanceof Error ? err.message : 'Failed to read source zip file.');
        return;
      }
    }

    const preparedItems = values.map((value, index) => {
      const renderedPrompt = replaceVariableTokens(promptTemplate, variableToken, value).trim();
      return {
        id: index + 1,
        value,
        title: `Auto title from prompt (${value})`,
        prompt: renderedPrompt,
        status: 'pending' as const,
        message: '',
        projectUrl: '',
      };
    });

    setBatchItems(preparedItems);
    setRunningBatch(true);
    runningBatchRef.current = true;
    setStopRequested(false);
    stopRequestedRef.current = false;

    try {
      for (const item of preparedItems) {
        if (stopRequestedRef.current) break;

        setBatchItems((prev) =>
          prev.map((existing) =>
            existing.id === item.id ? { ...existing, status: 'running', message: '' } : existing
          )
        );

        const result = await agent.runAgent('', item.prompt, {
          model: selectedModelRef.current,
          autoGenerateTitle: true,
          useRawDescription: true,
          sourceZipName: zipFile.name,
          sourceZipContentBase64: sourceZipContent,
        });

        const resolvedStatus: ZipBatchItemStatus =
          result.message === 'Stopped by user'
            ? 'stopped'
            : result.status === 'complete'
              ? 'complete'
              : result.status;

        setBatchItems((prev) =>
          prev.map((existing) =>
            existing.id === item.id
              ? {
                ...existing,
                status: resolvedStatus,
                message: result.message,
                projectUrl: result.projectUrl || '',
              }
              : existing
          )
        );

        if (resolvedStatus === 'stopped') {
          stopRequestedRef.current = true;
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 1200));
      }
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : 'Batch run failed.');
    } finally {
      setRunningBatch(false);
      runningBatchRef.current = false;
      setStopRequested(false);
      stopRequestedRef.current = false;
    }
  }, [agent, promptTemplate, session, valuesText, variableName, zipBase64, zipFile]);

  const completedCount = batchItems.filter((item) => item.status === 'complete').length;
  const failedCount = batchItems.filter((item) => item.status === 'failed' || item.status === 'error').length;
  const runningCount = batchItems.filter((item) => item.status === 'running').length;

  return (
    <>
      <div className="card p-6 mb-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="sourceZip" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
              Source mini-app zip
            </label>
            <input
              id="sourceZip"
              type="file"
              accept=".zip,application/zip"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setZipFile(file);
                setZipBase64('');
              }}
              disabled={runningBatch}
              className="input"
            />
            <p className="mt-1 text-xs text-theme-text-muted">
              The same zip is attached to every browser-agent run.
            </p>
          </div>

          <div>
            <label htmlFor="batchVarName" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
              Variable name
            </label>
            <input
              id="batchVarName"
              type="text"
              value={variableName}
              onChange={(e) => setVariableName(e.target.value)}
              disabled={runningBatch}
              className="input font-mono"
              placeholder="language"
            />
            <p className="mt-1 text-xs text-theme-text-muted">
              Use it in templates as <span className="font-mono">%{normalizeVariableName(variableName) || 'var_name'}%</span>.
            </p>
          </div>

          <div>
            <label htmlFor="promptTemplate" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
              Prompt template
            </label>
            <textarea
              id="promptTemplate"
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              disabled={runningBatch}
              className="input min-h-[140px] font-mono text-sm"
              placeholder={'Example:\nUse the attached source mini-app zip as a baseline.\nTranslate the entire UI and all prompts to %language%.\nPreserve existing functionality.'}
              rows={7}
            />
          </div>

          <div>
            <label htmlFor="variableValues" className="block text-sm font-medium text-theme-text-secondary mb-1.5">
              Variable values (one per line)
            </label>
            <textarea
              id="variableValues"
              value={valuesText}
              onChange={(e) => setValuesText(e.target.value)}
              disabled={runningBatch}
              className="input min-h-[170px] font-mono text-sm"
              placeholder={'Spanish\nFrench\nGerman'}
              rows={8}
            />
          </div>

          {batchError && (
            <p className="text-xs text-theme-error">{batchError}</p>
          )}

          <div className="flex items-center gap-3">
            <div className="relative" ref={modelDropdownRef}>
              <button
                type="button"
                onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                disabled={runningBatch}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-sm"
                title="Select coding model"
              >
                <Cpu className="h-3.5 w-3.5 text-theme-text-muted" />
                <span className="font-medium text-theme-text">
                  {AGENT_MODELS.find(m => m.id === selectedModel)?.shortName || 'Kimi'}
                </span>
                <ChevronDown className={'h-3.5 w-3.5 text-theme-text-muted transition-transform ' + (modelDropdownOpen ? 'rotate-180' : '')} />
              </button>
              {modelDropdownOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-52 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                  {AGENT_MODELS.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => handleModelChange(model.id)}
                      className={'w-full flex items-center justify-between px-3 py-2 text-left hover:bg-theme-surface-hover transition-colors ' +
                        (selectedModel === model.id ? 'bg-theme-surface-hover' : '')
                      }
                    >
                      <div className="text-sm font-medium text-theme-text">{model.name}</div>
                      {selectedModel === model.id && <Check className="h-3.5 w-3.5 text-accent-500" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1" />

            {!runningBatch ? (
              <button
                onClick={startBatch}
                className="btn btn-primary flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Play className="h-4 w-4" />
                Start batch
              </button>
            ) : (
              <button
                onClick={requestStop}
                className="btn btn-danger flex items-center gap-2"
              >
                <Square className="h-4 w-4" />
                {stopRequested ? 'Stopping after current...' : 'Stop batch'}
              </button>
            )}
          </div>
        </div>
      </div>

      {(agent.running || agent.logs.length > 0) && (
        <div className="mb-6">
          <AgentOutputPanel agent={agent} />
        </div>
      )}

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-theme-text">Batch runs</h3>
          <div className="text-xs text-theme-text-muted">
            Complete: {completedCount} | Failed: {failedCount} | Running: {runningCount}
          </div>
        </div>
        {batchItems.length === 0 ? (
          <p className="text-sm text-theme-text-muted">No batch runs yet.</p>
        ) : (
          <div className="space-y-2">
            {batchItems.map((item) => (
              <div key={item.id} className="card px-4 py-3 flex items-center gap-3">
                <span className="text-xs font-mono text-theme-text-muted w-7">{item.id}.</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-theme-text truncate">{item.title}</p>
                  <p className="text-xs text-theme-text-muted truncate">{item.value}</p>
                  {item.message && (
                    <p className="text-xs text-theme-text-secondary truncate">{item.message}</p>
                  )}
                </div>
                {item.projectUrl && (
                  <a
                    href={item.projectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-emerald-500 hover:text-emerald-400 underline flex-shrink-0"
                  >
                    Open project
                  </a>
                )}
                <span className={`text-xs px-2 py-1 rounded-full border flex-shrink-0 ${
                  item.status === 'complete'
                    ? 'text-theme-success border-theme-success-border bg-theme-success-bg'
                    : item.status === 'running'
                      ? 'text-blue-500 border-blue-500/30 bg-blue-500/10'
                      : item.status === 'pending'
                        ? 'text-theme-text-muted border-theme-border-muted bg-theme-surface'
                        : item.status === 'stopped'
                          ? 'text-amber-500 border-amber-500/30 bg-amber-500/10'
                          : 'text-theme-error border-theme-error-border bg-theme-error-bg'
                }`}>
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// Backlog section (generic)
// ============================================================================

function BacklogSection({
  title,
  icon,
  items,
  color,
  onDelete,
}: {
  title: string;
  icon: React.ReactNode;
  items: AgentBacklogItem[];
  color: string;
  onDelete?: (id: string) => void;
}) {
  return (
    <div>
      <div className={`flex items-center gap-2 ${color} mb-3`}>
        {icon}
        <h3 className="text-sm font-medium">{title} ({items.length})</h3>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="card px-4 py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-theme-text truncate">{item.title}</p>
              {item.description && (
                <p className="text-xs text-theme-text-muted truncate">{item.description}</p>
              )}
            </div>
            <span className="text-xs text-theme-text-muted flex-shrink-0">{formatItemDate(item.created_at)}</span>
            {onDelete && (
              <button
                onClick={() => onDelete(item.id)}
                className="p-1 text-theme-text-muted hover:text-theme-error transition-colors flex-shrink-0"
                title="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Agent Log Modal
// ============================================================================

function AgentLogModal({ item, onClose }: { item: AgentBacklogItem; onClose: () => void }) {
  const logEntries = (item.agent_log || []) as Array<{
    type: string;
    timestamp: string;
    data: Record<string, unknown>;
  }>;

  const [screenshotIndex, setScreenshotIndex] = useState(-1);
  const [screenshotUrls, setScreenshotUrls] = useState<string[]>([]);
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);

  const loadScreenshots = async () => {
    if (!item.screenshot_paths || item.screenshot_paths.length === 0) return;
    setLoadingScreenshots(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const urls: string[] = [];
      for (const path of item.screenshot_paths) {
        urls.push(`${supabaseUrl}/storage/v1/object/authenticated/agent-backlog/${path}`);
      }
      setScreenshotUrls(urls);
      if (urls.length > 0) setScreenshotIndex(0);
    } catch (err) {
      console.error('Failed to load screenshots:', err);
    } finally {
      setLoadingScreenshots(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-theme-bg border border-theme-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col m-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-theme-border">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-theme-text truncate">{item.title}</h3>
            {item.error_message && (
              <p className="text-sm text-amber-500 dark:text-amber-400 truncate">{item.error_message}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {item.screenshot_paths && item.screenshot_paths.length > 0 && (
              <button
                onClick={loadScreenshots}
                disabled={loadingScreenshots}
                className="btn btn-secondary text-xs flex items-center gap-1"
              >
                {loadingScreenshots ? <LoadingSpinner size="sm" /> : <Monitor className="h-3.5 w-3.5" />}
                Screenshots ({item.screenshot_paths.length})
              </button>
            )}
            <button onClick={onClose} className="p-1 text-theme-text-muted hover:text-theme-text">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Screenshots */}
        {screenshotUrls.length > 0 && screenshotIndex >= 0 && (
          <div className="p-4 border-b border-theme-border">
            <div className="flex items-center gap-2 mb-2">
              <Monitor className="h-4 w-4 text-theme-text-muted" />
              <span className="text-sm font-medium text-theme-text">Screenshots</span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={() => setScreenshotIndex((prev) => Math.max(0, prev - 1))}
                  disabled={screenshotIndex <= 0}
                  className="p-1 rounded-md border border-theme-border-muted text-theme-text-muted hover:text-theme-text disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs text-theme-text-muted tabular-nums min-w-[3.5ch] text-center">
                  {screenshotIndex + 1}/{screenshotUrls.length}
                </span>
                <button
                  onClick={() => setScreenshotIndex((prev) => Math.min(screenshotUrls.length - 1, prev + 1))}
                  disabled={screenshotIndex >= screenshotUrls.length - 1}
                  className="p-1 rounded-md border border-theme-border-muted text-theme-text-muted hover:text-theme-text disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="relative bg-theme-bg rounded-lg overflow-hidden border border-theme-border-muted" style={{ maxHeight: '300px' }}>
              <img
                src={screenshotUrls[screenshotIndex]}
                alt={"Screenshot " + (screenshotIndex + 1)}
                className="w-full h-auto"
              />
            </div>
          </div>
        )}

        {/* Log entries */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1 font-mono text-xs">
          {logEntries.length === 0 ? (
            <p className="text-theme-text-muted text-center py-8">No log entries recorded.</p>
          ) : (
            logEntries.map((entry, index) => {
              const time = new Date(entry.timestamp).toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              });

              let colorClass = 'text-theme-text-secondary';
              let prefix = '';
              let content = '';

              switch (entry.type) {
                case 'status':
                  colorClass = 'text-blue-500 dark:text-blue-400';
                  prefix = 'STATUS';
                  content = String(entry.data.message || '');
                  break;
                case 'tool_call':
                  colorClass = 'text-purple-500 dark:text-purple-400';
                  prefix = 'CALL';
                  {
                    const name = String(entry.data.name || 'unknown_tool');
                    const reason = String(entry.data.reason || '').trim();
                    const argsPreview = JSON.stringify(entry.data.args || {}).slice(0, 120);
                    content = reason
                      ? name + ' | reason: ' + reason + ' | args: ' + argsPreview
                      : name + '(' + argsPreview + ')';
                  }
                  break;
                case 'tool_result':
                  colorClass = 'text-theme-text-muted';
                  prefix = 'RESULT';
                  content = entry.data.name + ' \u2192 ' + String(entry.data.result || '').slice(0, 200);
                  break;
                case 'agent_thought':
                  colorClass = 'text-amber-500 dark:text-amber-400';
                  prefix = 'THINK';
                  content = String(entry.data.text || '');
                  break;
                case 'complete':
                  colorClass = 'text-theme-success';
                  prefix = 'DONE';
                  content = String(entry.data.summary || 'Task complete');
                  break;
                case 'failed':
                  colorClass = 'text-amber-500 dark:text-amber-400';
                  prefix = 'FAIL';
                  content = String(entry.data.reason || 'Task failed');
                  break;
                case 'error':
                  colorClass = 'text-theme-error';
                  prefix = 'ERROR';
                  content = String(entry.data.message || 'Unknown error');
                  break;
                default:
                  content = JSON.stringify(entry.data);
              }

              return (
                <div key={index} className={`px-2 py-1 rounded hover:bg-theme-surface/50 ${colorClass}`}>
                  <span className="text-theme-text-muted mr-2">{time}</span>
                  <span className="font-semibold mr-2">[{prefix}]</span>
                  <span className="break-all">{content}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Shared Agent Output Panel (screenshot + logs side by side)
// ============================================================================

function AgentOutputPanel({ agent }: { agent: ReturnType<typeof useAgentRunner> }) {
  const {
    running,
    logs,
    finalStatus,
    finalMessage,
    projectUrl,
    logsExpanded,
    setLogsExpanded,
    logEndRef,
    screenshotState,
    setScreenshotState,
    currentScreenshot,
    canGoPrev,
    canGoNext,
  } = agent;

  if (!running && logs.length === 0 && finalStatus === 'idle') return null;

  return (
    <>
      {/* Final Status Banner */}
      {finalStatus !== 'idle' && (
        <div className={`flex items-center gap-3 p-4 rounded-lg mb-6 ${finalStatus === 'complete'
          ? 'bg-theme-success-bg border border-theme-success-border text-theme-success'
          : finalStatus === 'failed'
            ? 'bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400'
            : 'bg-theme-error-bg border border-theme-error-border text-theme-error'
          }`}>
          {finalStatus === 'complete' ? (
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
          ) : finalStatus === 'failed' ? (
            <XCircle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium">{finalMessage}</p>
            {projectUrl && (
              <p className="text-xs mt-1">
                Private project URL:{' '}
                <a
                  href={projectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:opacity-80 break-all"
                >
                  {projectUrl}
                </a>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Main content: Screenshot + Logs side by side */}
      {(running || logs.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Screenshot Panel */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Monitor className="h-4 w-4 text-theme-text-muted" />
              <h3 className="text-sm font-medium text-theme-text">
                Browser View
              </h3>
              <div className="ml-auto flex items-center gap-3">
                {currentScreenshot?.label && (
                  <span className="text-xs text-theme-text-muted">
                    {currentScreenshot.label}
                  </span>
                )}
                {screenshotState.items.length > 0 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() =>
                        setScreenshotState((prev) =>
                          prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev
                        )
                      }
                      className={`p-1 rounded-md border border-theme-border-muted text-theme-text-muted transition-colors ${canGoPrev
                        ? 'hover:text-theme-text hover:border-theme-border'
                        : 'opacity-50 cursor-not-allowed'
                        }`}
                      disabled={!canGoPrev}
                      aria-label="Previous screenshot"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-theme-text-muted tabular-nums min-w-[3.5ch] text-center">
                      {screenshotState.index + 1}/{screenshotState.items.length}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setScreenshotState((prev) =>
                          prev.index < prev.items.length - 1 ? { ...prev, index: prev.index + 1 } : prev
                        )
                      }
                      className={`p-1 rounded-md border border-theme-border-muted text-theme-text-muted transition-colors ${canGoNext
                        ? 'hover:text-theme-text hover:border-theme-border'
                        : 'opacity-50 cursor-not-allowed'
                        }`}
                      disabled={!canGoNext}
                      aria-label="Next screenshot"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="relative bg-theme-bg rounded-lg overflow-hidden border border-theme-border-muted" style={{ minHeight: '300px' }}>
              {currentScreenshot ? (
                <img
                  src={`data:image/png;base64,${currentScreenshot.image}`}
                  alt="Browser screenshot"
                  className="w-full h-auto"
                />
              ) : (
                <div className="flex items-center justify-center h-[300px] text-theme-text-muted">
                  {running ? (
                    <div className="flex flex-col items-center gap-2">
                      <LoadingSpinner size="md" />
                      <span className="text-sm">Waiting for screenshot...</span>
                    </div>
                  ) : (
                    <span className="text-sm">No screenshots yet</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Logs Panel */}
          <div className="card p-4 flex flex-col" style={{ maxHeight: '700px' }}>
            <button
              className="flex items-center gap-2 mb-3 w-full text-left"
              onClick={() => setLogsExpanded(!logsExpanded)}
            >
              <Terminal className="h-4 w-4 text-theme-text-muted" />
              <h3 className="text-sm font-medium text-theme-text">
                Agent Log
              </h3>
              <span className="text-xs text-theme-text-muted ml-1">
                ({logs.length} entries)
              </span>
              <span className="ml-auto">
                {logsExpanded ? (
                  <ChevronUp className="h-4 w-4 text-theme-text-muted" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-theme-text-muted" />
                )}
              </span>
            </button>
            {logsExpanded && (
              <div className="flex-1 overflow-y-auto space-y-1 font-mono text-xs">
                {logs.map((entry) => (
                  <LogEntryRow key={entry.id} entry={entry} />
                ))}
                {running && (
                  <div className="flex items-center gap-2 px-2 py-1 text-theme-text-muted">
                    <LoadingSpinner size="sm" />
                    <span>Agent running...</span>
                  </div>
                )}
                <div ref={logEndRef} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Log entry row
// ============================================================================

function LogEntryRow({ entry }: { entry: LogEntry }) {
  const time = entry.timestamp.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  let colorClass = 'text-theme-text-secondary';
  let prefix = '';
  let content = '';

  switch (entry.type) {
    case 'status':
      colorClass = 'text-blue-500 dark:text-blue-400';
      prefix = 'STATUS';
      content = String(entry.data.message || '');
      break;
    case 'tool_call': {
      colorClass = 'text-purple-500 dark:text-purple-400';
      prefix = 'CALL';
      {
        const name = String(entry.data.name || 'unknown_tool');
        const reason = String(entry.data.reason || '').trim();
        const argsPreview = JSON.stringify(entry.data.args || {}).slice(0, 120);
        content = reason
          ? `${name} | reason: ${reason} | args: ${argsPreview}`
          : `${name}(${argsPreview})`;
      }
      break;
    }
    case 'tool_result':
      colorClass = 'text-theme-text-muted';
      prefix = 'RESULT';
      content = `${entry.data.name} \u2192 ${String(entry.data.result || '').slice(0, 200)}`;
      break;
    case 'agent_thought':
      colorClass = 'text-amber-500 dark:text-amber-400';
      prefix = 'THINK';
      content = String(entry.data.text || '');
      break;
    case 'complete':
      colorClass = 'text-theme-success';
      prefix = 'DONE';
      content = String(entry.data.summary || 'Task complete');
      break;
    case 'failed':
      colorClass = 'text-amber-500 dark:text-amber-400';
      prefix = 'FAIL';
      content = String(entry.data.reason || 'Task failed');
      break;
    case 'error':
      colorClass = 'text-theme-error';
      prefix = 'ERROR';
      content = String(entry.data.message || 'Unknown error');
      break;
    default:
      content = JSON.stringify(entry.data);
  }

  return (
    <div className={`px-2 py-1 rounded hover:bg-theme-surface/50 ${colorClass}`}>
      <span className="text-theme-text-muted mr-2">{time}</span>
      <span className="font-semibold mr-2">[{prefix}]</span>
      <span className="break-all">{content}</span>
    </div>
  );
}
